import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody } from './ui/Card';
import { api } from '../api/client';
import { endpoints } from '../api/endpoints';
import { OrderPeriodFilter } from './dashboard/OrderPeriodFilter';
import { OrdersByTimeChart } from './dashboard/OrdersByTimeChart';
import {
  buildOrdersAnalyticsParams,
  defaultBucketForPeriod,
  formatInr,
  formatPeriodRange,
} from './dashboard/dashboardPeriodUtils';
import {
  SalesRankBarChart,
  SalesRevenueByStatusChart,
  SalesTopDesignsTable,
} from './sales/SalesCharts';

function KpiTile({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm min-w-[140px]">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-900 tabular-nums mt-1">{value}</p>
      {hint ? <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p> : null}
    </div>
  );
}

function Sales() {
  const [period, setPeriod] = useState('30D');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const chartBucket = useMemo(() => defaultBucketForPeriod(period), [period]);

  const loadInsights = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (period === 'CUSTOM' && (!customFrom || !customTo)) return;

    setLoading(true);
    setError('');
    try {
      const qs = buildOrdersAnalyticsParams({
        period,
        from: customFrom,
        to: customTo,
        bucket: chartBucket,
      });
      const { data } = await api.get(`${endpoints.sales.insights}?${qs}`);
      setInsights(data);
    } catch (e) {
      setError(e?.message || 'Failed to load sales insights');
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, [period, chartBucket, customFrom, customTo]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const rangeLabel = formatPeriodRange(insights);

  const analyticsForChart = insights
    ? {
        chartBuckets: insights.chartBuckets,
        bucketType: insights.bucketType,
      }
    : null;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Card>
        <CardBody className="space-y-6 pt-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Period</h2>
              {rangeLabel ? <p className="text-xs text-slate-500 mt-0.5">{rangeLabel}</p> : null}
            </div>
            <OrderPeriodFilter
              period={period}
              onPeriodChange={setPeriod}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />
          </div>

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50/90 text-rose-800 px-4 py-3 text-sm">
              {error}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 sm:gap-3">
            <KpiTile
              label="Total revenue"
              value={insights ? formatInr(insights.totalRevenue) : loading ? '…' : '—'}
            />
            <KpiTile
              label="Orders"
              value={insights ? Number(insights.orderTotalCount || 0).toLocaleString() : loading ? '…' : '—'}
            />
            <KpiTile
              label="Pieces"
              value={insights ? Number(insights.totalPieces || 0).toLocaleString() : loading ? '…' : '—'}
            />
            <KpiTile
              label="Avg order value"
              value={insights ? formatInr(insights.avgOrderValue) : loading ? '…' : '—'}
            />
            <KpiTile
              label="Delivered"
              value={insights ? formatInr(insights.deliveredRevenue) : loading ? '…' : '—'}
              hint={insights ? `${insights.deliveredOrders} orders` : undefined}
            />
            <KpiTile
              label="Dispatched"
              value={insights ? formatInr(insights.dispatchedRevenue) : loading ? '…' : '—'}
              hint={insights ? `${insights.dispatchedOrders} orders` : undefined}
            />
            <KpiTile
              label="In pipeline"
              value={insights ? formatInr(insights.pipelineRevenue) : loading ? '…' : '—'}
              hint={insights ? `${insights.pipelineOrders} orders` : undefined}
            />
            <KpiTile
              label="Cancelled"
              value={insights ? formatInr(insights.cancelledRevenue) : loading ? '…' : '—'}
              hint={insights ? `${insights.cancelledOrders} orders` : undefined}
            />
          </div>
        </CardBody>
      </Card>

      <section className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-sm">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Orders & revenue over time</h2>
          <p className="text-xs text-slate-500 mt-0.5">Stacked order counts with total amount trend line</p>
        </div>
        <OrdersByTimeChart analytics={analyticsForChart} loading={loading} />
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <SalesRevenueByStatusChart rows={insights?.revenueByStatus} loading={loading} />
        <SalesRankBarChart title="Top parties by revenue" rows={insights?.topParties} loading={loading} />
        <SalesRankBarChart title="Top categories by revenue" rows={insights?.topCategories} loading={loading} />
        <SalesRankBarChart title="Top concepts by revenue" rows={insights?.topConcepts} loading={loading} />
        <SalesRankBarChart title="Brokers & direct sales" rows={insights?.topBrokers} loading={loading} />
        <SalesTopDesignsTable rows={insights?.topDesigns} loading={loading} />
      </div>
    </div>
  );
}

export default Sales;
